import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { CloseRounded } from "@mui/icons-material";
import { useEffect, useState } from "react";
import apiService from "../../../../../../../service/Api";
import { tokens } from "../../../../../../../theme";


export interface IntroductionLetterTemplate {
  id: number;
  name: string;
  category: string;
  category_display: string;
  body_template: string;
  placeholders: string[];
  is_active: boolean;
  is_default_template: boolean;
}

export interface ProposalData {
  clientName?: string;
  deceasedName?: string;
  eventTitle?: string;
  serviceDate?: string;
  location?: string;
}

export const replacePlaceholders = (
  template: string,
  data?: ProposalData,
): string => {
  
  if (!data) return template;

  const replacements: Record<string, string> = {
    "{client_name}": data.clientName || "",
    "{deceased_name}": data.deceasedName || "",
    "{event_title}": data.eventTitle || "",
    "{service_date}": data.serviceDate || "",
    "{service_venue}": data.location || "",
    "{current_date}": new Date().toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }),
  };

  let result = template;
  Object.entries(replacements).forEach(([placeholder, value]) => {
    if (value) {
      result = result.replace(
        new RegExp(placeholder.replace(/[{}]/g, "\\$&"), "g"),
        value,
      );
    }
  });
  return result;
};

export const stripHtml = (html: string) =>
  html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();

interface TemplatePickerDialogProps {
  open: boolean;
  onClose: () => void;
  onApply: (body: string) => void;
  bookingType: string;
  proposalData?: ProposalData;
}


export default function TemplatePickerDialog({
  open,
  onClose,
  onApply,
  bookingType,
  proposalData,
}: TemplatePickerDialogProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [templates, setTemplates] = useState<IntroductionLetterTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<IntroductionLetterTemplate | null>(
    null,
  );

  const category = bookingType.toLowerCase();
  const filtered = templates.filter(
    (t) => t.is_active && t.category === category,
  );

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    apiService
      .get<IntroductionLetterTemplate[]>(
        "/api/configurations/introduction-letter-templates/",
      )
      .then((res) => setTemplates(res.data))
      .catch((err) => console.error("Failed to fetch templates:", err))
      .finally(() => setLoading(false));
  }, [open]);

  const handleApply = () => {
    if (!selected) return;
    const replaced = stripHtml(
      replacePlaceholders(selected.body_template, proposalData),
    );
    onApply(replaced);
    setSelected(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            sx={{ fontWeight: 700, fontSize: "16px", color: colors.primary[100] }}
          >
            Choose Template
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseRounded sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress size={24} sx={{ color: colors.primary[400] }} />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 2,
                minHeight: 320,
              }}
            >
              {/* Template list */}
              <Stack spacing={1}>
                <Typography
                  sx={{
                    fontSize: "11px",
                    fontWeight: 700,
                    color: colors.primary[100],
                    textTransform: "uppercase",
                    mb: 0.5,
                  }}
                >
                  Templates
                </Typography>

                {filtered.length === 0 ? (
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: colors.primary[100],
                      py: 3,
                      textAlign: "center",
                    }}
                  >
                    No templates for this booking type.
                  </Typography>
                ) : (
                  filtered.map((t) => (
                    <Box
                      key={t.id}
                      onClick={() => setSelected(t)}
                      sx={{
                        borderRadius: "10px",
                        px: 2,
                        py: 1.5,
                        cursor: "pointer",
                        border:
                          selected?.id === t.id
                            ? `2px solid  ${colors.primary[400]}`
                            : `2px solid ${colors.primary[800]}`,
                        bgcolor: colors.primary[900],
                        transition: "all 0.15s",
                        "&:hover": { borderColor: colors.primary[500] },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 600,
                          color: colors.primary[100],
                          mb: 0.3,
                        }}
                      >
                        {t.name}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "11px",
                          color: "text.secondary",
                          overflow: "hidden",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {stripHtml(
                          replacePlaceholders(t.body_template, proposalData),
                        ).substring(0, 100)}
                        ...
                      </Typography>
                    </Box>
                  ))
                )}
              </Stack>

              {/* Preview panel */}
              <Box
                sx={{
                  bgcolor: "background.paper",
                  borderRadius: "10px",
                  p: 2,
                  overflowY: "auto",
                  maxHeight: 360,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "11px",
                    fontWeight: 700,
                    color: "text.secondary",
                    textTransform: "uppercase",
                    mb: 1,
                  }}
                >
                  Preview
                </Typography>
                {selected ? (
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: colors.primary[100],
                      lineHeight: 1.8,
                      whiteSpace: "pre-line",
                    }}
                  >
                    {stripHtml(
                      replacePlaceholders(selected.body_template, proposalData),
                    )}
                  </Typography>
                ) : (
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "text.secondary",
                      fontStyle: "italic",
                    }}
                  >
                    Select a template to preview
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Apply button */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Box
                onClick={handleApply}
                sx={{
                  bgcolor: selected ? colors.primary[400] : colors.primary[800],
                  color: selected ? "white" : colors.primary[100],
                  borderRadius: "10px",
                  px: 3,
                  py: 1,
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: selected ? "pointer" : "not-allowed",
                  transition: "bgcolor 0.15s",
                  "&:hover": selected ? { bgcolor: colors.primary[500] } : {},
                }}
              >
                Apply Template
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}